import { i18n } from "@tellescope/i18n";
import type { RoutesConfig } from "./types";
import {
  Calendar,
  CalendarCheck,
  FileText,
  HeartPulse,
  Inbox,
  LayoutDashboard,
  Settings,
  Sparkles,
  Ticket,
  UsersRound,
} from "lucide-react";

// Base configuration without component references - safe for hooks to import
// This contains only metadata: paths, labels, icons, protection settings, etc.

const INBOX_THREAD_ROUTES_CONFIG = {
  "/:threadId": {
    showInNav: false,
    isProtected: true,
    hideMainLayout: true,
  },
} as const satisfies RoutesConfig;

const INBOX_THREAD_ROUTES_WITH_COMPOSE_CONFIG = {
  ...INBOX_THREAD_ROUTES_CONFIG,
  "/new": {
    showInNav: false,
    isProtected: true,
    hideMainLayout: true,
  },
} as const satisfies RoutesConfig;

const INBOX_CHANNEL_ROUTES_CONFIG = {
  "/email": {
    showInNav: false,
    isProtected: true,
    hideMainLayout: true,
    subRoutes: INBOX_THREAD_ROUTES_WITH_COMPOSE_CONFIG,
  },
  "/groupmms": {
    showInNav: false,
    hideMainLayout: true,
    isProtected: true,
    subRoutes: INBOX_THREAD_ROUTES_CONFIG,
  },
  "/sms": {
    showInNav: false,
    isProtected: true,
    hideMainLayout: true,
    subRoutes: INBOX_THREAD_ROUTES_WITH_COMPOSE_CONFIG,
  },
  "/chat": {
    showInNav: false,
    isProtected: true,
    hideMainLayout: true,
    subRoutes: INBOX_THREAD_ROUTES_WITH_COMPOSE_CONFIG,
  },
  "/phone": {
    showInNav: false,
    isProtected: true,
    hideMainLayout: true,
    subRoutes: INBOX_THREAD_ROUTES_CONFIG,
  },
  "/zendesk": {
    showInNav: false,
    isProtected: true,
    hideMainLayout: true,
    subRoutes: INBOX_THREAD_ROUTES_WITH_COMPOSE_CONFIG,
  },
} as const satisfies RoutesConfig;

export const ROUTES_CONFIG = {
  "/login": {
    isProtected: false,
    hideMainLayout: true,
  },
  "/mfa": {
    isProtected: false,
    hideMainLayout: true,
  },
  "/complete-sso": {
    isProtected: false,
    hideMainLayout: true,
  },
  "/e-signature-terms": {
    isProtected: false,
    hideMainLayout: true,
  },
  "/mobile-app": {
    isProtected: false,
    hideMainLayout: true,
    subRoutes: {
      "/login": {
        isProtected: false,
        hideMainLayout: true,
      },
      "/mfa": {
        isProtected: false,
        hideMainLayout: true,
      },
      "/success": {
        isProtected: false,
        hideMainLayout: true,
      },
    },
  },
  "/dashboard": {
    showInNav: true,
    label: () => i18n.t("Dashboard", { ns: "routing" }),
    isProtected: true,
    icon: <LayoutDashboard />,
    featureName: "dashboard",
  },
  "/ask-ai": {
    showInNav: true,
    label: () => i18n.t("Ask AI", { ns: "routing" }),
    isProtected: true,
    icon: <Sparkles />,
    featureName: "ai",
    modelForAccess: "ai_conversations",
  },
  "/contacts": {
    showInNav: true,
    label: () => i18n.t("Contacts", { ns: "routing" }),
    isProtected: true,
    icon: <UsersRound />,
    subRoutes: {
      "/views": {
        isProtected: true,
        subRoutes: {
          "/:viewId": {
            isProtected: true,
          },
        },
      },
      "/timeline": {
        showInNav: false,
        isProtected: true,
        subRoutes: {
          "/:enduserId": {
            showInNav: false,
            isProtected: true,
          },
        },
      },
    },
    modelForAccess: "endusers",
    featureName: "contacts",
  },
  "/contact-details": {
    showInNav: false,
    isProtected: true,
    modelForAccess: "endusers",
    subRoutes: {
      "/:enduserId": {
        showInNav: false,
        isProtected: true,
        subRoutes: {
          "/:viewId": {
            showInNav: false,
            isProtected: true,
            subRoutes: {
              "/:blockId": {
                showInNav: false,
                isProtected: true,
              },
            },
          },
        },
      },
    },
  },
  "/tickets": {
    showInNav: true,
    label: () => i18n.t("Tickets", { ns: "routing" }),
    isProtected: true,
    icon: <Ticket />,
    hideMainLayout: true,
    subRoutes: {
      "/details": {
        showInNav: false,
        isProtected: true,
        subRoutes: {
          "/:ticketId": {
            showInNav: false,
            isProtected: true,
          },
        },
      },
      "/views": {
        isProtected: true,
        subRoutes: {
          "/:viewId": {
            showInNav: false,
            isProtected: true,
          },
        },
      },
      "/queues": {
        isProtected: true,
        subRoutes: {
          "/:queueId": {
            showInNav: false,
            isProtected: true,
          },
        },
      },
    },
    modelForAccess: "tickets",
    featureName: "tickets",
  },
  "/templates": {
    isProtected: true,
    showInNav: true,
    label: () => i18n.t("Templates", { ns: "routing" }),
    icon: <FileText />,
    modelForAccess: "templates",
    featureName: "templates",
    subRoutes: {
      "/views": {
        isProtected: true,
        showInNav: false,
        subRoutes: {
          "/:viewId": {
            isProtected: true,
            showInNav: false,
          },
        },
      },
      // "/snippets": {
      //   isProtected: true,
      //   showInNav: false,
      //   modelForAccess: "message_template_snippets",
      // },
    },
  },
  "/calendar": {
    isProtected: true,
    showInNav: true,
    label: () => i18n.t("Calendar", { ns: "routing" }),
    icon: <Calendar />,
    shouldShow: ({ selectedOrganization }) => selectedOrganization?.plan?.type !== "Healthie-Text",
    subRoutes: {
      "/availability": {
        isProtected: true,
        showInNav: false,
      },
      "/locations": {
        isProtected: true,
        showInNav: false,
        modelForAccess: "appointment_locations",
      },
      "/templates": {
        isProtected: true,
        showInNav: false,
        modelForAccess: "calendar_event_templates",
      },
      "/booking-pages": {
        isProtected: true,
        showInNav: false,
        modelForAccess: "appointment_booking_pages",
      },
      "/view": {
        isProtected: true,
        showInNav: false,
        subRoutes: {
          "/:viewId": {
            isProtected: true,
            showInNav: false,
            subRoutes: {
              "/month": {
                isProtected: true,
                showInNav: false,
                subRoutes: {
                  "/:year": {
                    isProtected: true,
                    showInNav: false,
                    subRoutes: {
                      "/:month": {
                        isProtected: true,
                        showInNav: false,
                      },
                    },
                  },
                },
              },
              "/list": {
                isProtected: true,
                showInNav: false,
                subRoutes: {
                  "/:year": {
                    isProtected: true,
                    showInNav: false,
                    subRoutes: {
                      "/:month": {
                        isProtected: true,
                        showInNav: false,
                      },
                    },
                  },
                },
              },
              "/day": {
                isProtected: true,
                showInNav: false,
                subRoutes: {
                  "/:year": {
                    isProtected: true,
                    showInNav: false,
                    subRoutes: {
                      "/:month": {
                        isProtected: true,
                        showInNav: false,
                        subRoutes: {
                          "/:day": {
                            isProtected: true,
                            showInNav: false,
                          },
                        },
                      },
                    },
                  },
                },
              },
              "/week": {
                isProtected: true,
                showInNav: false,
                subRoutes: {
                  "/:year": {
                    isProtected: true,
                    showInNav: false,
                    subRoutes: {
                      "/:month": {
                        isProtected: true,
                        showInNav: false,
                        subRoutes: {
                          "/:day": {
                            isProtected: true,
                            showInNav: false,
                          },
                        },
                      },
                    },
                  },
                },
              },
              "/year": {
                isProtected: true,
                showInNav: false,
                subRoutes: {
                  "/:year": {
                    isProtected: true,
                    showInNav: false,
                  },
                },
              },
            },
          },
        },
      },
    },
    modelForAccess: "calendar_events",
    featureName: "calendar",
  },
  "/appointment-reminders": {
    showInNav: true,
    isProtected: true,
    label: () => i18n.t("Appointment Reminders", { ns: "routing" }),
    icon: <CalendarCheck />,
    shouldShow: ({ selectedOrganization }) => selectedOrganization?.plan?.type === "Healthie-Text",
    modelForAccess: "calendar_event_templates",
  },
  "/vitals": {
    showInNav: true,
    isProtected: true,
    label: () => i18n.t("Vitals", { ns: "routing" }),
    icon: <HeartPulse />,
    shouldShow: ({ selectedOrganization }) => !!selectedOrganization?.settings?.endusers?.showFullVitalsTab,
    modelForAccess: "enduser_observations",
    hideMainLayout: true,
    subRoutes: {
      "/views": {
        isProtected: true,
        subRoutes: {
          "/:viewId": {
            showInNav: false,
            isProtected: true,
          },
        },
      },
    },
    featureName: "vitals",
  },
  "/settings": {
    showInNav: true,
    isProtected: true,
    label: () => i18n.t("Settings", { ns: "routing" }),
    icon: <Settings />,
    showSubrouteIndentBorder: true,
    shouldShrinkSubroutes: false,
    subRoutes: {
      "/organizations": {
        showInNav: true,
        isProtected: true,
        label: () => i18n.t("Organizations", { ns: "routing" }),
        adminOnly: true,
      },
      "/email": {
        showInNav: true,
        isProtected: true,
        label: () => i18n.t("Email", { ns: "routing" }),
        adminOnly: true,
        shouldShow: ({ selectedOrganization }) => selectedOrganization?.plan?.type !== "Healthie-Text",
      },
      "/phone": {
        showInNav: true,
        isProtected: true,
        label: () => i18n.t("Phone", { ns: "routing" }),
        adminOnly: true,
        subRoutes: {
          "/numbers": {
            showInNav: false,
            isProtected: true,
          },
          "/usage": {
            showInNav: false,
            isProtected: true,
          },
          "/logs": {
            showInNav: false,
            isProtected: true,
            subRoutes: {
              "/views": {
                showInNav: false,
                isProtected: true,
                subRoutes: {
                  "/:viewId": {
                    showInNav: false,
                    isProtected: true,
                  },
                },
              },
            },
          },
        },
      },
      "/team": {
        showInNav: true,
        isProtected: true,
        label: () => i18n.t("Team", { ns: "routing" }),
        adminOnly: true,
        subRoutes: {
          "/views": {
            showInNav: false,
            isProtected: true,
            subRoutes: {
              "/:viewId": {
                showInNav: false,
                isProtected: true,
              },
            },
          },
          "/roles": {
            showInNav: false,
            isProtected: true,
            shouldShow: ({ selectedOrganization }) => selectedOrganization?.plan?.type !== "Healthie-Text",
          },
          "/configuration": {
            showInNav: false,
            isProtected: true,
          },
        },
      },
      "/other": {
        showInNav: true,
        isProtected: true,
        label: () => i18n.t("Other Settings", { ns: "routing" }),
        subRoutes: {
          "/preferences": {
            showInNav: false,
            isProtected: true,
          },
        },
      },
    },
    featureName: "settings",
  },
  "/messaging": {
    showInNav: true,
    isProtected: true,
    label: () => i18n.t("Messaging", { ns: "routing" }),
    icon: <Inbox />,
    subRoutes: {
      "/inbox": {
        showInNav: true,
        label: () => i18n.t("Inbox", { ns: "routing" }),
        isProtected: true,
        subRoutes: INBOX_CHANNEL_ROUTES_CONFIG,
      },
      "/drafts-sent": {
        showInNav: true,
        label: () => i18n.t("Drafts & Sent", { ns: "routing" }),
        isProtected: true,
        hideMainLayout: true,
        subRoutes: {
          "/:view": {
            showInNav: false,
            isProtected: true,
          },
        },
      },
      "/archive": {
        showInNav: true,
        label: () => i18n.t("Archive", { ns: "routing" }),
        isProtected: true,
        subRoutes: INBOX_CHANNEL_ROUTES_CONFIG,
      },
      "/trash": {
        showInNav: true,
        label: () => i18n.t("Trash", { ns: "routing" }),
        isProtected: true,
        subRoutes: INBOX_CHANNEL_ROUTES_CONFIG,
      },
      "/team-chat": {
        showInNav: true,
        isProtected: true,
        shouldShrinkSubroutes: true,
        subRoutes: {
          "/:chatroomId": {
            showInNav: false,
            isProtected: true,
          },
        },
      },
    },
    featureName: "messaging",
  },
} as const satisfies RoutesConfig;
